import { useEffect, useState } from 'react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useUserProfile } from '@backstage/plugin-user-settings';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useRoleSetupStyles } from './role-setup.styles';
import { roles } from '../../constants';
import { ResearchPanelInterest } from '../ResearchPanelInterest/ResearchPanelInterest';
import { useRoleSetupTranslationRef } from '../../translation';
import { useUpdateUserRole } from '../../hooks';

type RoleSetupProps = {
  onSubmitSuccess: () => void;
};

export const RoleSetup = ({ onSubmitSuccess }: RoleSetupProps) => {
  const classes = useRoleSetupStyles();
  const { t } = useTranslationRef(useRoleSetupTranslationRef);
  const { profile } = useUserProfile();
  const {
    updateUserRole,
    loading: updateUserRoleLoading,
    error: updateUserRoleError,
    value: updateUserRoleValue,
  } = useUpdateUserRole();

  const [selectedRole, setSelectedRole] = useState('');
  const [submittedRole, setSubmittedRole] = useState('');

  const roleRows = Array.from({ length: 4 }, (_, index) =>
    roles.slice(index * 3, index * 3 + 3),
  );

  const handleBack = () => {
    setSubmittedRole('');
  };

  const handleSubmit = async (researchPanelInterest: boolean) => {
    if (!profile.email || !submittedRole) {
      return;
    }

    await updateUserRole({
      email: profile.email,
      role: submittedRole,
      researchPanelInterest,
    });
  };

  useEffect(() => {
    if (!updateUserRoleLoading && !updateUserRoleError && updateUserRoleValue) {
      onSubmitSuccess();
    }
  }, [
    onSubmitSuccess,
    updateUserRoleError,
    updateUserRoleLoading,
    updateUserRoleValue,
  ]);

  return (
    <>
      {!submittedRole && (
        <Box className={classes.container}>
          <Box className="headerWrapper">
            <Typography variant="body1" className="headerTitle">
              {t('roleSetup.headerTitle')}
            </Typography>
            <Typography variant="body1" className="headerDesc">
              {t('roleSetup.headerDesc1')}
              <br />
              {t('roleSetup.headerDesc2')}
            </Typography>
          </Box>

          <Box className="roleWrapper">
            {roleRows.map((row, rowIndex) => (
              <Box className="roleRowWrapper" key={`row-${rowIndex}`}>
                {row.map(({ name, Icon }) => (
                  <Box
                    className={`roleItemWrapper ${selectedRole === name ? 'selected' : ''}`}
                    key={name}
                    onClick={() => setSelectedRole(name)}
                  >
                    <Icon className="icon" />
                    <Typography variant="body1" className="label">
                      {name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          <Box className="roleButtonWrapper">
            <Button
              variant="contained"
              className="buttonFilled"
              onClick={() => setSubmittedRole(selectedRole)}
              disabled={!selectedRole}
              disableRipple
            >
              {t('button.next')}
            </Button>

            {selectedRole && (
              <Box className={classes.infoWrapper}>
                <ErrorOutlineIcon />
                <Typography variant="body1" className="infoText">
                  {t('roleSetup.info')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {submittedRole && (
        <ResearchPanelInterest
          handleBack={handleBack}
          handleSubmit={handleSubmit}
          isSubmitting={updateUserRoleLoading}
        />
      )}
    </>
  );
};
